import { IMenusClient } from './IMenusClient';
import { Menu, MenuListResponse, CreateMenuRequest, MenuStatus, MenuRecipeSummary, MenuType, MenuCycle } from './menusTypes';
import { menus } from '../../../../mocks/data/menusData';
import { recipeV2Client } from '../recipeV2';
import { CarbonRating } from '../recipeV2/recipeV2Types';

/** In-memory mocked client for the Menus demo screen, following the same pattern as
 * RecipeV2ClientMocked - no MSW handlers, just an in-code store seeded from
 * src/mocks/data/menusData.ts. Delegates to recipeV2Client for per-dish carbon/dietary data
 * so Menus and Recipes stay consistent rather than duplicating that data here. */
export class MenusClientMocked implements IMenusClient {
  private menus: Menu[] = [...menus];
  private nextId = Math.max(...menus.map((m) => m.id)) + 1;

  // NOTE: methods are arrow-function class fields (not prototype methods) so `this` stays
  // bound to the instance even when a consuming hook destructures them off the client object
  // (e.g. `const { getMenus } = useMenusApi();`) - a plain `async getMenus() {}` method loses
  // its `this` in that call pattern and every `this.menus` access throws, which was silently
  // swallowed by the hook's try/catch and rendered as an empty menus list with no console error.
  getMenus = async (search: string, status: string, page: number, pageSize: number): Promise<MenuListResponse> => {
    const term = search.trim().toLowerCase();
    const filtered = this.menus.filter((menu) => {
      const matchesSearch = !term || menu.name.toLowerCase().includes(term);
      const matchesStatus = !status || menu.status === status;
      return matchesSearch && matchesStatus;
    });

    const start = page * pageSize;
    return {
      data: filtered.slice(start, start + pageSize),
      totalCount: filtered.length,
    };
  };

  getMenuDetail = async (id: number): Promise<Menu> => {
    const menu = this.menus.find((m) => m.id === id);
    if (!menu) throw new Error('Menu not found');
    return menu;
  };

  createMenu = async (data: CreateMenuRequest): Promise<Menu> => {
    const newMenu: Menu = {
      id: this.nextId++,
      name: data.name,
      description: data.description,
      status: MenuStatus.Draft,
      menuType: MenuType.Lunch,
      menuCycle: MenuCycle.SingleDay,
      servicePeriods: [],
      courses: [],
      sites: [],
      targetMarginPercent: 60,
      versionHistory: [{ version: 1, date: new Date().toISOString().slice(0, 10), changeSummary: 'Initial draft created.' }],
      publishing: { publishStatus: MenuStatus.Draft, channels: [] },
    };
    this.menus.push(newMenu);
    return newMenu;
  };

  publishMenu = async (id: number): Promise<void> => {
    const menu = this.menus.find((m) => m.id === id);
    if (!menu) return;
    menu.status = MenuStatus.Live;
    menu.publishing.publishStatus = MenuStatus.Live;
    menu.publishing.publishedDate = new Date().toISOString().slice(0, 10);
  };

  getRecipeSummariesForMenu = async (id: number): Promise<MenuRecipeSummary[]> => {
    const menu = this.menus.find((m) => m.id === id);
    if (!menu) return [];

    const recipeIds = Array.from(new Set(menu.courses.flatMap((c) => c.dishes.map((d) => d.recipeId))));
    const summaries = await Promise.all(recipeIds.map(async (recipeId) => {
      try {
        const detail = await recipeV2Client.getRecipeDetail(recipeId);
        return {
          recipeId,
          recipeName: detail.customerFacingName ?? detail.description,
          carbonRating: detail.carbonScore ?? CarbonRating.C,
          dietaryAttributes: detail.dietaryAttributes,
          costPerPortion: detail.costPerPortion,
          dataCompleteness: detail.dataCompleteness,
        };
      } catch {
        return null;
      }
    }));

    return summaries.filter((s): s is NonNullable<typeof s> => s !== null);
  };
}
